import React, { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import { LANDING } from "@/constants/testIds";

/**
 * Reusable legal-document layout. Body content is rendered as a
 * stack of titled paragraphs and bulleted lists.
 */
const LegalLayout = ({ index, label, title, breadcrumb, body, testId, footnote }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main
        data-testid={testId}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index={index}
          label={label}
          title={title}
          breadcrumb={breadcrumb}
        />

        <section className="py-20 lg:py-28">
          <div className="max-w-[960px] mx-auto px-6 lg:px-12">
            <div className="mb-12 font-mono-label text-[10px] text-[var(--kr-text-muted)]">
              Last updated · {new Date().toLocaleDateString("en-GB", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>

            <div className="space-y-14">
              {body.map((section, i) => (
                <section key={i}>
                  <div className="flex items-center gap-4 mb-5">
                    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
                  </div>
                  <h2 className="font-serif-display text-[var(--kr-text)] text-[1.7rem] lg:text-[2rem] leading-[1.2] mb-6">
                    {section.heading}
                  </h2>
                  <div className="space-y-5 text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85]">
                    {(section.paragraphs || []).map((p, idx) => (
                      <p key={idx}>{p}</p>
                    ))}
                    {section.bullets ? (
                      <ul className="space-y-3 pl-1">
                        {section.bullets.map((b, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <span
                              aria-hidden
                              className="mt-[10px] block w-[5px] h-[5px] flex-shrink-0"
                              style={{
                                background: "var(--kr-gold)",
                                transform: "rotate(45deg)",
                              }}
                            />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </section>
              ))}
            </div>

            {footnote ? (
              <p className="mt-16 pt-10 border-t border-[var(--kr-line)] text-[12px] leading-[1.75] text-[var(--kr-text-dim)]">
                {footnote}
              </p>
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

const LEGAL_BODY = [
  {
    heading: "Important Notice",
    paragraphs: [
      "Kellermann Reid & Co. (the 'Firm' or the 'platform') is a private markets platform in strategic formation. The materials on this website are provided for general information purposes only and do not constitute an offer to sell, or a solicitation of an offer to subscribe for or buy, any security, fund interest, financial instrument or service in any jurisdiction. Any reference to the platform's intended strategies, mandates, vehicles or governance is preparatory in nature and is subject to documentation, regulatory authorisation and onboarding of eligible, sophisticated, professional or institutional investors.",
      "These materials are not directed at, intended for, or available to, any person in any jurisdiction where the publication or availability of such materials would be contrary to local law or regulation. Visitors are responsible for informing themselves about, and observing, all such restrictions.",
    ],
  },
  {
    heading: "Eligibility & Investor Status",
    paragraphs: [
      "Any future investment opportunities offered by the platform will be restricted to persons who qualify as sophisticated, professional, accredited or institutional investors under the applicable law of the jurisdiction in which they are domiciled or resident. The platform does not solicit interest from, and will not transact with, retail investors.",
    ],
  },
  {
    heading: "No Reliance · No Advice",
    paragraphs: [
      "Nothing on this website constitutes investment, legal, tax, accounting or other professional advice. Visitors should not rely on any statement or implication contained in these materials when making any investment or other decision and should obtain independent professional advice appropriate to their circumstances.",
    ],
  },
  {
    heading: "Forward-Looking Statements",
    paragraphs: [
      "Certain statements on this website are forward-looking. Such statements involve known and unknown risks, uncertainties and other factors which may cause actual outcomes to differ materially from those expressed or implied. The platform undertakes no obligation to update any forward-looking statement.",
    ],
  },
  {
    heading: "Working Titles · Vehicle Architecture",
    paragraphs: [
      "Vehicle and fund names referenced on this site (including those branded with the 'KR' prefix) are working titles. Final vehicle names, terms, capacity, structure and launch sequencing will be established in connection with documentation and regulatory authorisation.",
    ],
  },
  {
    heading: "Intellectual Property",
    paragraphs: [
      "The 'Kellermann Reid & Co.' name, the KR monogram and all related marks, copy, design and graphical elements are the property of the platform and its principals. Reproduction or use without prior written consent is prohibited.",
    ],
  },
  {
    heading: "Governing Law",
    paragraphs: [
      "Use of this website is governed by the laws of England and Wales, without regard to its conflict-of-laws principles. Visitors agree to submit to the exclusive jurisdiction of the courts of England and Wales for the resolution of any dispute arising out of or in connection with their use of this website.",
    ],
  },
];

const PRIVACY_BODY = [
  {
    heading: "Scope of this Notice",
    paragraphs: [
      "This Privacy Notice describes how Kellermann Reid & Co. ('we', 'us' or the 'Firm') collects, uses and protects personal information submitted via this website — including through the Contact form, the Investor Access request and other voluntary correspondence.",
    ],
  },
  {
    heading: "Information We Collect",
    paragraphs: [
      "We collect only the information you choose to provide to us, together with limited technical information generated by your interaction with the website.",
    ],
    bullets: [
      "Submitted information: name, email, firm / institution, role, subject, message, nature of interest and any optional notes.",
      "Technical information: anonymised pages viewed, approximate region, device type and timestamps, used solely to monitor the integrity of the site.",
    ],
  },
  {
    heading: "How We Use Information",
    paragraphs: [
      "Submitted information is used solely to respond to your enquiry, to assess eligibility for documented engagement, and to maintain a confidential record of correspondence.",
    ],
    bullets: [
      "Responding to enquiries and providing information requested.",
      "Assessing the suitability of further engagement on a confidential basis.",
      "Maintaining the platform's compliance, recordkeeping and audit obligations.",
    ],
  },
  {
    heading: "Confidentiality",
    paragraphs: [
      "Submitted information is treated as confidential. It is shared only with members of the partnership and with professional advisers, administrators and service providers engaged under appropriate confidentiality and data-protection terms.",
    ],
  },
  {
    heading: "Retention",
    paragraphs: [
      "We retain submitted information for as long as is necessary to respond to your enquiry, to maintain a record of our correspondence and to satisfy any applicable legal, regulatory or recordkeeping obligation.",
    ],
  },
  {
    heading: "Your Rights",
    paragraphs: [
      "Subject to applicable law, you may have rights to access, correct, restrict, object to or request deletion of personal information held about you. To exercise any such right, please contact investors@krprivatecapital.com.",
    ],
  },
  {
    heading: "Security",
    paragraphs: [
      "We maintain administrative, technical and organisational measures designed to protect personal information against unauthorised access, alteration or disclosure. No internet-based service can be guaranteed to be entirely secure.",
    ],
  },
];

const COOKIES_BODY = [
  {
    heading: "Our Use of Cookies",
    paragraphs: [
      "This website uses a minimal set of cookies and similar technologies to ensure the site functions correctly and to monitor the integrity of the experience. We do not use cookies for advertising or behavioural profiling.",
    ],
  },
  {
    heading: "Categories",
    bullets: [
      "Strictly Necessary: required for the website to function (e.g. routing, session continuity). These cannot be switched off.",
      "Functional: remember your acknowledgement of this Cookies Notice and your declared preferences.",
      "Analytics (optional): anonymised, aggregated usage statistics used to maintain and improve the website.",
    ],
  },
  {
    heading: "Managing Cookies",
    paragraphs: [
      "You may accept or decline non-essential cookies via the banner presented on first visit. Your preference is stored locally in your browser. You can change your preference at any time by clearing site data in your browser settings.",
    ],
  },
  {
    heading: "Third-Party Services",
    paragraphs: [
      "Where third-party services are used (for example, email delivery and form transmission), they are engaged under appropriate confidentiality and data-protection terms and only insofar as necessary to deliver the service requested.",
    ],
  },
];

export const LegalPage = () => (
  <LegalLayout
    index="Legal"
    label="Legal Notices · Disclaimers"
    breadcrumb="Legal"
    testId={LANDING.legalPage}
    title={
      <>
        Legal{" "}
        <span className="italic text-[var(--kr-gold)]">notices</span>.
      </>
    }
    body={LEGAL_BODY}
    footnote="© Kellermann Reid & Co. All rights reserved. This website and its contents are intended for institutional, professional and sophisticated investors only."
  />
);

export const PrivacyPage = () => (
  <LegalLayout
    index="Privacy"
    label="Privacy Notice · Data Handling"
    breadcrumb="Privacy"
    testId={LANDING.privacyPage}
    title={
      <>
        Privacy{" "}
        <span className="italic text-[var(--kr-gold)]">notice</span>.
      </>
    }
    body={PRIVACY_BODY}
    footnote="Direct any data protection enquiries to investors@krprivatecapital.com."
  />
);

export const CookiesPage = () => (
  <LegalLayout
    index="Cookies"
    label="Cookies Notice · Site Behaviour"
    breadcrumb="Cookies"
    testId={LANDING.cookiesPage}
    title={
      <>
        Cookies{" "}
        <span className="italic text-[var(--kr-gold)]">notice</span>.
      </>
    }
    body={COOKIES_BODY}
    footnote="If you have any question regarding our use of cookies, please contact investors@krprivatecapital.com."
  />
);

export default LegalPage;
